/******************************************** IMPORTS *********************************************/
import {isDevelopment} from 'env-var-helpers';
import {configure as configureMobx} from 'mobx';

import {appStateFactory, SignupFormStore} from '../models/models';

import {translateFn} from '@canimmunize/core-imports';
import {getPolyglot} from './polyglot-factory';
import {config} from '../helpers/config-utils';

/***************************************** GRAB LANGUAGE ******************************************/
// Get language (for use in initializing javelin & moment)
import {getLangFromUrlPathname} from 'mad-utils/lib/react';
const currentLanguage = getLangFromUrlPathname(window.location.pathname);

/**************************************** GLOBAL LIBRARIES ****************************************/
/*--------------- Set up moment (date handling) ---------------*/
// Import moment & make French-Canadian moment locale available
import moment from 'moment';

// Sets moment locale to French if `/fr/` present in URL
// Defaults to English
if (window.location.pathname.includes(`/fr/`)) {
    require('moment/locale/fr-ca');
    moment.locale(`fr-ca`);
}

/*--------------------------------------------------------------*/

// Allow Node's inspect commands to work in browser
(window as any).inspect = require('object-inspect');

// Globally bind Mousetrap library to mousetrap-pause plugin
import '../helpers/mousetrap-with-plugins';

/************************ Import Javelin AFTER global libs are configured *************************/
import {SSTemp, javelinFactory} from '@canimmunize/javelinscript';

/************************************* ACTIVATE GLOBAL STYLES *************************************/
import '../styles/global-styles.scss';
import '../hocs/react-toastr/src/lib/toastr.scss';
import '!style-loader!css-loader!react-datepicker/dist/react-datepicker.css';

// IIFE a11y focus outline fix
import './outline-a11y';

/*************************** AUGMENT GLOBAL PROTOTYPES / ADD POLYFILLS ****************************/
// Note: import value is a dummy, but having an actual export returned from a module is
// required for Typescript to apply any defined global styles within said module
import * as AUGMENT_GLOBAL_PROTOTYPES from '../../../shared/augment-global-prototypes/augment-global-prototypes'; // tslint:disable-line

// This log statement ensures that AUGMENT_GLOBAL_PROTOTYPES is imported and consequently
// that those additions to the prototypes are applied
console.log(`AUGMENT_GLOBAL_PROTOTYPES:`, AUGMENT_GLOBAL_PROTOTYPES);

/******************************* ACTIVATE DEVELOPMENT MODE GLOBALS ********************************/
// Import value is a dummy - just required for Typescript to actually pull the module in
import {attachDevModeGlobals, bindAppStateToDevGlobals} from './dev-mode-globals';
const devGlobalsNs = attachDevModeGlobals();

/************************* ACTIVATE REQUEST/RESPONSE LOGGING IN DEV MODE **************************/
if (isDevelopment) require('axios-response-logger');

/***************************************** BUILD POLYGLOT *****************************************/
/**
 * Polyglot data grabbed from global location
 */
export const polyglot = getPolyglot();

/****************************** ENVIRONMENT VARIABLE SETUP/HANDLING *******************************/
/**
 * Amount to pad environment variables when logging
 */
const envVarRPad = 14;

/**
 *
 * Must preserve process.env.SOME_VALUE syntax below
 *
 * Cannot be done dynamically - Webpack must see actual "process.env.SOMETHING"
 * values to inject the corresponding environment variables
 *
 * ---------------------------------------------------------------------------
 * WARNING
 * =======
 * DO NOT MODIFY "process.env.SOME_VALUE" VALUES!!!
 *
 * The object values must be *precisely* process.env.SOME_VALUE format
 *
 * e.g. Don't do this:
 *
 *     // BAD!!! BELOW WON'T WORK!!!
 *     const nodeEnv = `NODE_ENV`;
 *     export const envVars = {
 *         // FAIL: For the Webpack injection to work, the code must EXPLICITY
 *         // be `process.env.NODE_ENV` - no abstractions allowed
 *         NODE_ENV: process.env[nodeEnv],
 *         ...
 *     };
 *     // BAD!!! ABOVE WILL NOT WORK!!!
 * ---------------------------------------------------------------------------
 *
 * *** Note: add these in env-var-store as well ***
 */
export const envVars = {
    NODE_ENV: process.env.NODE_ENV,
    LOG_LEVEL: process.env.LOG_LEVEL,
    APP_VERSION: process.env.APP_VERSION,
    RELEASE_ENV: process.env.RELEASE_ENV,
    BUILD_NUMBER: process.env.BUILD_NUMBER,
    ALGOLIA_APPID: process.env.ALGOLIA_APPID,
    ALGOLIA_APIKEY: process.env.ALGOLIA_APIKEY,
    DEV_FULLNAME: process.env.DEV_FULLNAME,
    DEV_USERNAME: process.env.DEV_USERNAME,
    DEV_PASSWORD: process.env.DEV_PASSWORD,
};

/**
 * Log injected environment variables
 */
if (process.env.NODE_ENV !== `production`) {
    console.log(
        `ENVIRONMENT VARIABLES:` +
            Object.keys(envVars).reduce(
                (acc, name) =>
                    `${acc}\n    process.env.${name.padRight(envVarRPad)}: ${envVars[name]}`,
                ``
            )
    );
}

/*************************************** STATE STORE SETUP ****************************************/
// Turn MobX strict mode on
configureMobx({enforceActions: `observed`});

// Clear SSTemp values (temporary sessionStore values, explicitly marked by e.g. [-TEMP-])
SSTemp.clearAll();

/**
 * Initialize & return Javelin & app state store
 * Also binds state to dev namespace in dev mode
 *
 * Sets up the singleton Javelin facade object, containing:
 *     Sync manager / interface    -  javelin.syncManager
 *     Data interface              -  javelin.javelinData
 *     Data manager                -  javelin.dataManager
 *     Javelin manager             -  javelin.javelinManager
 *     Method for record building  -  javelin.buildRecord
 * Uses it to build app state store, then returns app state store & Javelin object
 */
export const hydrateAppState = javelinFactory({
    clientVersion: `${envVars.APP_VERSION}.${envVars.BUILD_NUMBER}-${envVars.RELEASE_ENV}`,
    translateFn,
    currentLanguage,
})
    .then(javelin => {
        const newAppState = appStateFactory(
            envVars,
            javelin.syncManager,
            javelin.javelinManager,
            javelin.javelinData
        );
        bindAppStateToDevGlobals();
        return {newAppState, javelin};
    })
    .catch(err => {
        console.error(`hydrateAppState ERROR :: err:`, err);
    });

/**
 * Signup form state store instance (singleton)
 */
export const signupFormModel = SignupFormStore.new(envVars);
